/**
 * Reconcile estimate PDF/preview totals with stored option pricing + deposit payment logic.
 */
import { normalizeOrderDiscountFraction } from './pricing.js';
function round2(n) {
    return Number((Number(n) || 0).toFixed(2));
}
function lineExtension(item) {
    const direct = Number(item.totalPrice ?? item.total);
    if (Number.isFinite(direct) && direct !== 0)
        return direct;
    return (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0);
}
/**
 * Build a display-ready totals breakdown that always reconciles:
 * items (+ markup) − discount + tax = total, and amountDue follows deposit rules.
 */
export function buildEstimatePdfSummary(input) {
    const itemsSubtotal = round2((input.items || []).reduce((sum, item) => sum + lineExtension(item), 0));
    const markupPercent = Number(input.markupPercent) || 0;
    const discountPercent = normalizeOrderDiscountFraction(input.discountPercent);
    const taxRate = Number(input.taxRate) || 0;
    const markupAmount = markupPercent > 0 ? round2(itemsSubtotal * markupPercent) : 0;
    const afterMarkup = round2(itemsSubtotal + markupAmount);
    const taxAmount = round2(Number(input.taxAmount) || 0);
    const storedTotal = Number(input.totalAmount);
    const total = Number.isFinite(storedTotal) && storedTotal > 0
        ? round2(storedTotal)
        : round2(afterMarkup + taxAmount);
    const storedSubtotal = Number(input.subtotal);
    const subtotal = Number.isFinite(storedSubtotal) && storedSubtotal >= 0
        ? round2(storedSubtotal)
        : round2(Math.max(0, total - taxAmount));
    // Prefer explicit percent discount; otherwise derive from gap between gross and stored subtotal
    let discountAmount = discountPercent > 0 ? round2(afterMarkup * discountPercent) : 0;
    if (discountAmount <= 0) {
        const derived = round2(afterMarkup - subtotal);
        if (derived > 0.009)
            discountAmount = derived;
    }
    const depositRequired = Boolean(input.depositRequired);
    const depositAmount = round2(Number(input.depositAmount) || 0);
    const useDeposit = depositRequired && depositAmount > 0;
    return {
        itemsSubtotal,
        markupPercent,
        markupAmount,
        discountPercent,
        discountAmount,
        subtotal,
        taxAmount,
        taxRate,
        total,
        amountDue: useDeposit ? depositAmount : total,
        amountDueLabel: useDeposit ? 'Deposit Due' : 'Amount Due',
        showMarkup: markupAmount > 0.009,
        showDiscount: discountAmount > 0.009
    };
}
//# sourceMappingURL=estimatePdfSummary.js.map